import { IGenericError, UnsualPeriodResponse } from '@libs/entity-lib';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import * as fromCustomer from './unusual-period.actions';

export interface State extends EntityState<UnsualPeriodResponse> {
  loading: boolean;
  error: IGenericError | null;
  // Histórico paginado (aba "Histórico"). Mantido em paralelo ao
  // EntityAdapter (que guarda apenas os futuros/em andamento).
  historyContent: UnsualPeriodResponse[];
  historyLoading: boolean;
  historyPage: number;
  historyTotalPages: number;
  historyTotalElements: number;
  historyHasMore: boolean;
}

const adapter: EntityAdapter<UnsualPeriodResponse> = createEntityAdapter<UnsualPeriodResponse>({
  selectId: (unsualPeriod: UnsualPeriodResponse) => unsualPeriod.id,
});

export const initialState: State = adapter.getInitialState({
  loading: false,
  error: null,
  historyContent: [],
  historyLoading: false,
  historyPage: 0,
  historyTotalPages: 0,
  historyTotalElements: 0,
  historyHasMore: true,
});

export const reducer = createReducer(
  initialState,
  on(fromCustomer.fetchUnusualPeriod, (state) => ({ ...state, loading: true, error: null })),
  on(fromCustomer.fetchUnusualPeriodSuccessfuly, (state, { unsualPeriodList }) => {
    // setAll, não addMany: o PUT do backend é delete + recreate e devolve um ID
    // novo, então addMany deixava o registro antigo no store e o horário especial
    // editado aparecia duplicado na lista até o próximo reload.
    return adapter.setAll(unsualPeriodList, {
      ...state,
      loading: false,
      error: null,
    });
  }),
  on(fromCustomer.fetchUnusualPeriodFailed, (state, { error }) =>
    adapter.setAll([], { ...state, loading: false, error })
  ),

  // Add
  on(fromCustomer.addUnusualPeriod, (state) => ({ ...state, loading: true, error: null })),
  on(fromCustomer.addUnusualPeriodSuccessfuly, (state) => ({
    ...state,
    loading: false,
    error: null,
  })),
  on(fromCustomer.addUnusualPeriodFailed, (state, { error }) =>
    adapter.setAll([], { ...state, loading: false, error })
  ),

  // Add em lote (um horário especial por dia selecionado). O effect refaz o
  // fetch, então aqui basta soltar o loading — nada de setAll([]) no erro, que
  // apagaria a lista inteira por causa de um dia que falhou.
  on(fromCustomer.addUnusualPeriodBatch, (state) => ({ ...state, loading: true, error: null })),
  on(fromCustomer.addUnusualPeriodBatchSuccessfuly, (state) => ({
    ...state,
    loading: false,
    error: null,
  })),
  on(fromCustomer.addUnusualPeriodBatchPartial, (state) => ({
    ...state,
    loading: false,
    error: null,
  })),
  on(fromCustomer.addUnusualPeriodBatchFailed, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Update
  on(fromCustomer.updateUnusualPeriod, (state) => ({ ...state, loading: true, error: null })),
  on(fromCustomer.updateUnusualPeriodSuccessfuly, (state) => ({
    ...state,
    loading: false,
    error: null,
  })),
  on(fromCustomer.updateUnusualPeriodFailed, (state, { error }) =>
    adapter.setAll([], { ...state, loading: false, error })
  ),

  // Remove
  on(fromCustomer.removeUnusualPeriod, (state) => ({ ...state, loading: true, error: null })),
  on(fromCustomer.removeUnusualPeriodSuccessfuly, (state, { id }) => {
    return adapter.removeOne(id, {
      ...state,
      loading: false,
      error: null,
    });
  }),
  on(fromCustomer.removeUnusualPeriodFailed, (state, { error }) =>
    adapter.setAll([], { ...state, loading: false, error })
  ),

  // Histórico paginado
  on(fromCustomer.fetchUnusualPeriodHistory, (state, { reset }) => ({
    ...state,
    historyLoading: true,
    historyContent: reset ? [] : state.historyContent,
    historyPage: reset ? 0 : state.historyPage,
  })),
  on(fromCustomer.fetchUnusualPeriodHistorySuccessfuly, (state, { response, reset }) => {
    const incoming = response?.content ?? [];
    const merged = reset ? incoming : [...state.historyContent, ...incoming];
    return {
      ...state,
      historyLoading: false,
      historyContent: merged,
      historyPage: response?.number ?? state.historyPage,
      historyTotalPages: response?.totalPages ?? 0,
      historyTotalElements: response?.totalElements ?? 0,
      historyHasMore: !(response?.last ?? true),
    };
  }),
  on(fromCustomer.fetchUnusualPeriodHistoryFailed, (state, { error }) => ({
    ...state,
    historyLoading: false,
    error,
  }))
);

export const { selectAll } = adapter.getSelectors();
